import { CACHE_KEYS } from '@dmp/constant/cacheKeys';
import type { InternalAppsPermission } from '@dmp/types/auth.types';

import { parseJwt } from './auth';
import { getCachedTenantConfig } from './cache';

export enum Environment {
  DEV = 'dev',
  QA = 'qa',
  STAGING = 'stage',
  PROD = 'prod',
}

export const DOMAIN_MAP = {
  [Environment.DEV]: 'https://data-dev.centific.com',
  [Environment.QA]: 'https://data-qa.centific.com',
  [Environment.STAGING]: 'https://data-stg.centific.com',
  [Environment.PROD]: 'https://data.centific.com',
};

export const getEnv = () => {
  switch (window.location.origin) {
    case DOMAIN_MAP[Environment.PROD]:
      return Environment.PROD;
    case DOMAIN_MAP[Environment.DEV]:
      return Environment.DEV;
    case DOMAIN_MAP[Environment.QA]:
      return Environment.QA;
    case DOMAIN_MAP[Environment.STAGING]:
      return Environment.STAGING;
    // consider a developer's own machine as dev
    default:
      return Environment.DEV;
  }
};

export const getInternalAppsPermission = (): InternalAppsPermission => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);

  if (!token) {
    return {
      dataset_management: false,
      telemetry: false,
      analytics: false,
      agency_catalog: false,
    };
  }

  const decodedToken = parseJwt(token);
  // Top-level claim on current DMP-minted tokens; falls back to the legacy nested shape for any
  // still-circulating token minted before the flatten.
  const internalApps = decodedToken.internal_apps ?? decodedToken.extra_data?.internal_apps;

  return {
    dataset_management: internalApps?.dataset_management?.access || false,
    telemetry: internalApps?.telemetry?.access || false,
    analytics: internalApps?.analytics?.access || false,
    agency_catalog: internalApps?.agency_catalog?.access || false,
  };
};

export const isCentificUser = (email?: string) => {
  return email?.toLowerCase().endsWith('@centific.com');
};

/** Read the dataset-management role from the JWT's top-level `internal_apps.dataset_management.role` (falls back to the legacy nested `extra_data.internal_apps` shape); the DMP-owned Uploader role is NOT in the JWT and comes from `GET /api/v1/admin/me/` instead. */
export const getDatasetMgmtRole = (): string => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return '';
  try {
    const decoded = parseJwt(token);
    const internalApps = decoded.internal_apps ?? decoded.extra_data?.internal_apps;
    return internalApps?.dataset_management?.role || '';
  } catch {
    return '';
  }
};

/** Whether the JWT grants dataset-management admin access. */
export const isDatasetMgmtAdmin = (): boolean => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return false;
  try {
    const decoded = parseJwt(token);
    const internalApps = decoded.internal_apps ?? decoded.extra_data?.internal_apps;
    return !!internalApps?.dataset_management?.access;
  } catch {
    return false;
  }
};

/** Whether the unified RBAC layer should surface on the UI; falls back to the plain JWT `internal_apps`-driven flow when the tenant config's `entra_auth_enabled` is false/missing. */
export const isRbacEnabled = (): boolean => {
  return Boolean(getCachedTenantConfig()?.config.entra_auth_enabled);
};

/** Whether the JWT itself marks the caller as RBAC Administrator via the top-level `is_rbac_admin` claim (falls back to the legacy nested `extra_data.is_rbac_admin` shape); informational/display-only, since the backend independently re-derives this as the actual authorization source of truth. */
export const isRbacAdminFromToken = (): boolean => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return false;
  try {
    const decoded = parseJwt(token);
    return !!(decoded.is_rbac_admin ?? decoded.extra_data?.is_rbac_admin);
  } catch {
    return false;
  }
};

/** Whether the JWT marks the caller as an Uploader via the top-level `is_uploader` claim (falls back to the legacy nested `extra_data.is_uploader` shape); a token-mint-time snapshot that is informational/display-only, since the backend independently re-derives Uploader status for actual authorization. */
export const isUploaderFromToken = (): boolean => {
  const token = localStorage.getItem(CACHE_KEYS.TOKEN);
  if (!token) return false;
  try {
    const decoded = parseJwt(token);
    return !!(decoded.is_uploader ?? decoded.extra_data?.is_uploader);
  } catch {
    return false;
  }
};
